import { useRef } from "react";

interface Props {
  testid: string;
  label: string;
  hint: string;
  accept: string;
  icon: React.ReactNode;
  file: File | null;
  disabled: boolean;
  onPick: (f: File) => void;
}

const fmtSize = (bytes: number) => (bytes >= 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1)} MB` : `${Math.ceil(bytes / 1024)} KB`);

// Music file picker: fuchsia accent, shows the selected file name + size.
export function FilePicker({ testid, label, hint, accept, icon, file, disabled, onPick }: Props) {
  const ref = useRef<HTMLInputElement>(null);
  return (
    <button
      type="button"
      data-testid={testid}
      disabled={disabled}
      onClick={() => ref.current?.click()}
      className={
        "group flex w-full items-center gap-4 rounded-xl border p-4 text-left transition-[border-color,box-shadow,transform] duration-200 disabled:cursor-not-allowed disabled:opacity-50 " +
        (file
          ? "border-[#FF3DBE]/60 bg-[#12061A]/80 shadow-[0_0_30px_-12px_rgba(255,61,190,0.6)]"
          : "border-[#FF3DBE]/20 bg-[#05070D]/60 hover:-translate-y-px hover:border-[#FF3DBE]/60 hover:shadow-[0_0_30px_-12px_rgba(255,61,190,0.5)]")
      }
    >
      <span className="grid h-11 w-11 shrink-0 place-items-center rounded-lg border border-[#FF3DBE]/40 bg-[#0B0412] text-[#FF7AD5] transition-transform duration-200 group-hover:scale-105">
        {icon}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block font-display text-sm font-semibold uppercase tracking-wide">{label}</span>
        <span data-testid={`${testid}-name`} className="block truncate text-xs text-muted-foreground">
          {file ? `${file.name} · ${fmtSize(file.size)}` : hint}
        </span>
      </span>
      <input
        ref={ref}
        data-testid={`${testid}-input`}
        type="file"
        accept={accept}
        className="hidden"
        onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) onPick(f); }}
      />
    </button>
  );
}
